<template>
  <!--
    Every dropdown in the application is this one.

    It used to be a native <select> with `appearance: none`, which took the
    operating system's arrow off the closed control but left the open list to
    Windows: grey, square-cornered, its own font, ignoring the dark theme and
    the brand colour. One screen therefore looked like two applications the
    moment you clicked something. The list is drawn here now, from the same
    tokens as the rest of the interface, so it matches wherever it opens.

    What the platform gave away for free and has to be rebuilt by hand: keyboard
    navigation, type-ahead, opening upwards when the bottom of the window is
    close, and a list that escapes whatever has `overflow: hidden` around it —
    hence the Teleport, because the first version of this got clipped by the
    modal it lived in.
  -->
  <div
    ref="root"
    class="select"
    :class="[$attrs.class, { 'select--open': open, 'select--invalid': invalid, 'select--disabled': disabled }]"
    :style="$attrs.style"
  >
    <!--
      A mirror of the value in a real <select>, rendered only for the callers
      that asked for `name` or `required`. Those two are the whole of what the
      browser does for a form that a div cannot: submitting the field, and
      refusing to submit an empty one. It is never seen and never focused, but
      it must stay in the layout (opacity, not display:none) or the browser has
      nowhere to point the "please choose an option" bubble.
    -->
    <select
      v-if="mirrored"
      v-bind="formAttrs"
      class="select__mirror"
      tabindex="-1"
      aria-hidden="true"
    >
      <option value="" :selected="!selected" />
      <option
        v-for="option in normalised"
        :key="option.value"
        :value="option.value"
        :selected="option.value === modelValue"
      >{{ option.label }}</option>
    </select>

    <button
      v-bind="triggerAttrs"
      :id="controlId"
      ref="trigger"
      type="button"
      class="select__trigger"
      :role="useSearch ? undefined : 'combobox'"
      :disabled="disabled"
      :aria-haspopup="'listbox'"
      :aria-expanded="open"
      :aria-controls="open ? listId : undefined"
      :aria-activedescendant="!useSearch && open && active ? optionId(activeIndex) : undefined"
      :aria-invalid="invalid || undefined"
      @click="toggle"
      @keydown="onTriggerKey"
    >
      <span class="select__value" :class="{ 'select__value--empty': !selected }">
        {{ selected ? selected.label : (placeholder ?? '') }}
      </span>

      <svg
        class="select__arrow" width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </button>

    <Teleport to="body">
      <!-- En el móvil la lista es una hoja y esto es lo que hay detrás: tocarlo
           cierra con el CLICK y no con el pointerdown, que si no el mismo toque
           acaba en lo que había debajo (un botón, otra casilla). -->
      <div v-if="open && sheet" class="select__backdrop" @click="close()" />
      <Transition :name="`select-pop-${placement}`">
        <div
          v-if="open"
          ref="pop"
          class="select__pop"
          :class="`select__pop--${placement}`"
          :style="popStyle"
        >
          <!-- Long lists lose the one thing the native control did well: typing
               a few letters to get there. Below the threshold the trigger keeps
               that behaviour without a box; above it, four hundred time zones
               need something you can see what you typed into. -->
          <div v-if="useSearch" class="select__search">
            <input
              ref="search"
              v-model="query"
              type="text"
              class="select__search-input"
              role="combobox"
              autocomplete="off"
              spellcheck="false"
              aria-autocomplete="list"
              :aria-expanded="true"
              :aria-controls="listId"
              :aria-activedescendant="active ? optionId(activeIndex) : undefined"
              :placeholder="uiLabel('filterOptions')"
              :aria-label="uiLabel('filterOptions')"
              @keydown="onSearchKey"
            />
          </div>

          <ul :id="listId" class="select__list" role="listbox" :aria-labelledby="id">
            <li
              v-for="(option, index) in visible"
              :id="optionId(index)"
              :key="option.value"
              class="select__option"
              :class="{
                'select__option--active': index === activeIndex,
                'select__option--selected': option.value === modelValue,
                'select__option--disabled': option.disabled,
              }"
              role="option"
              :aria-selected="option.value === modelValue"
              :aria-disabled="option.disabled || undefined"
              @mousedown.prevent
              @mousemove="activeIndex = index"
              @click="choose(option)"
            >
              <span class="truncate">{{ option.label }}</span>

              <svg
                v-if="option.value === modelValue"
                class="select__tick" width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M4 12l5 5L20 6" />
              </svg>
            </li>

            <li v-if="!visible.length" class="select__none">{{ uiLabel('noOptions') }}</li>
          </ul>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, inject, nextTick, onBeforeUnmount, ref, useAttrs, useId, watch } from 'vue'

import { FIELD_ID } from '../utils/fieldId'

import { uiLabel } from '../utils/labels'
import { visibleBox } from '../utils/viewport'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  // Accepts ['a', 'b'] or [{ value, label, disabled }] so callers do not have to map.
  options: { type: Array, default: () => [] },
  placeholder: String,
  id: String,
  invalid: Boolean,
  // Left alone it decides for itself: a filter box is noise on four options and
  // the only usable way through four hundred.
  searchable: { type: Boolean, default: null },
})

// El id que pone la etiqueta del campo, cuando el llamante no ha dado uno.
const fieldId = inject(FIELD_ID, null)
const controlId = computed(() => props.id ?? fieldId)
const emit = defineEmits(['update:modelValue'])

const attrs = useAttrs()
const listId = useId()

const root = ref(null)
const trigger = ref(null)
const pop = ref(null)
const search = ref(null)

const open = ref(false)
const query = ref('')
const activeIndex = ref(-1)
const placement = ref('down')
const popStyle = ref({})

/**
 * En un teléfono la lista no cuelga del control: sale como hoja desde abajo.
 *
 * Colgada del control, en el móvil hacía cosas raras: se recolocaba con cada
 * scroll —también con el de su propia lista—, en las de más de diez opciones
 * enfocaba el buscador, salía el teclado, la ventana encogía y la lista
 * saltaba de abajo a arriba o se quedaba debajo del teclado. Una hoja abajo,
 * del ancho de la pantalla, no depende de dónde esté el control.
 */
const SHEET_QUERY = '(pointer: coarse) and (max-width: 640px)'
const sheet = ref(false)

const normalised = computed(() =>
  props.options.map(option =>
    option !== null && typeof option === 'object'
      ? { value: option.value, label: option.label ?? String(option.value), disabled: Boolean(option.disabled) }
      : { value: option, label: String(option), disabled: false }
  )
)

const selected = computed(() => normalised.value.find(option => option.value === props.modelValue))

const SEARCH_FROM = 10
const useSearch = computed(() => props.searchable ?? normalised.value.length > SEARCH_FROM)

const visible = computed(() => {
  const term = query.value.trim().toLowerCase()
  if (!useSearch.value || !term) return normalised.value
  return normalised.value.filter(option => String(option.label).toLowerCase().includes(term))
})

const active = computed(() => visible.value[activeIndex.value] ?? null)

// Where a caller's attributes land. The class and style belong to the control
// as a whole — a width goes on the wrapper, not on the button inside it. `name`
// and `required` are the form's business and only the mirror can honour them.
// Everything else (aria-describedby, title, data-*, a listener) describes the
// thing you actually click, which is the trigger.
const FORM_ONLY = new Set(['name', 'required'])

const disabled = computed(() => attrs.disabled !== undefined && attrs.disabled !== false)
const mirrored = computed(() => 'name' in attrs || 'required' in attrs)

const triggerAttrs = computed(() => Object.fromEntries(
  Object.entries(attrs).filter(([key]) => key !== 'class' && key !== 'style' && !FORM_ONLY.has(key))
))
const formAttrs = computed(() => ({
  name: attrs.name,
  required: attrs.required,
  disabled: disabled.value || undefined,
}))

const optionId = (index) => `${listId}-option-${index}`

/* ── Opening and closing ─────────────────────────────────────────────────── */

function toggle() {
  if (open.value) close()
  else show()
}

async function show() {
  if (disabled.value) return
  query.value = ''
  sheet.value = typeof window.matchMedia === 'function' && window.matchMedia(SHEET_QUERY).matches
  open.value = true
  startOnSelection()

  place()
  document.addEventListener('pointerdown', onPointerDown, true)
  window.addEventListener('resize', place)
  // La hoja no escucha nada: sigue al teclado por CSS (--keyboard-inset).
  if (!sheet.value) {
    // Capture, so a scroll inside a modal body or a table moves the list too.
    window.addEventListener('scroll', place, true)
  }

  await nextTick()
  // Again: clearing a leftover filter above puts the watcher on the first row,
  // and that runs between here and there.
  startOnSelection()
  place()
  // En la hoja, no: enfocar saca el teclado y tapa media lista antes de que
  // nadie haya pedido escribir. Quien quiera filtrar toca la caja.
  if (useSearch.value && !sheet.value) search.value?.focus()

  await nextTick()
  scrollActiveIntoView()
}

/** Open on what is already chosen, so the first arrow key moves from there. */
function startOnSelection() {
  activeIndex.value = Math.max(0, visible.value.findIndex(option => option.value === props.modelValue))
}

function close({ focusTrigger = false } = {}) {
  if (!open.value) return
  open.value = false
  detach()
  if (focusTrigger) trigger.value?.focus()
}

function detach() {
  document.removeEventListener('pointerdown', onPointerDown, true)
  window.removeEventListener('resize', place)
  window.removeEventListener('scroll', place, true)
}

function onPointerDown(event) {
  if (root.value?.contains(event.target) || pop.value?.contains(event.target)) return
  // La hoja la cierra su fondo, con el click (ver la plantilla).
  if (sheet.value) return
  close()
}

function choose(option) {
  if (option.disabled) return
  emit('update:modelValue', option.value)
  close({ focusTrigger: true })
}

onBeforeUnmount(detach)

/* ── Where the list goes ─────────────────────────────────────────────────── */

const GAP = 6
const MIN_HEIGHT = 168
const MAX_HEIGHT = 320
const MIN_WIDTH = 180
const EDGE = 8

function place() {
  const element = trigger.value
  if (!element) return

  if (sheet.value) {
    // La hoja la coloca su CSS (.select__pop--sheet) con --keyboard-inset y
    // --app-height: el teclado que sale o se va la mueve sin pasar por aquí.
    placement.value = 'sheet'
    popStyle.value = {}
    return
  }

  // Contra lo que se ve y no contra la ventana: con el teclado fuera (iOS)
  // `innerHeight` cuenta las teclas como sitio libre debajo del control.
  const box = visibleBox()
  const rect = element.getBoundingClientRect()
  const below = box.bottom - rect.bottom - GAP - EDGE
  const above = rect.top - box.top - GAP - EDGE

  // Downwards unless there is genuinely no room and there is more room up. A
  // list that flips on every few pixels of scroll is worse than a short one.
  const down = below >= MIN_HEIGHT || below >= above
  const width = Math.max(rect.width, MIN_WIDTH)

  placement.value = down ? 'down' : 'up'
  popStyle.value = {
    left: `${Math.max(EDGE, Math.min(rect.left, window.innerWidth - width - EDGE))}px`,
    width: `${width}px`,
    // Whatever room there is, capped: MIN_HEIGHT decides which way to open, it
    // does not get to push the list off a short window.
    maxHeight: `${Math.min(MAX_HEIGHT, Math.max(120, down ? below : above))}px`,
    ...(down
      ? { top: `${rect.bottom + GAP}px` }
      : { bottom: `${box.layoutHeight - rect.top + GAP}px` }),
  }
}

function scrollActiveIntoView() {
  pop.value?.querySelector('.select__option--active')?.scrollIntoView({ block: 'nearest' })
}

watch(activeIndex, () => nextTick(scrollActiveIntoView))
// Filtering shifts the list under the cursor; land on the first match so Enter
// always means something.
watch(query, () => { activeIndex.value = visible.value.length ? 0 : -1 })

/* ── Keyboard ────────────────────────────────────────────────────────────── */

function move(step) {
  const list = visible.value
  if (!list.length) return
  let index = activeIndex.value
  // Skip over disabled entries rather than stopping on them, and give up after
  // one full pass so a list of nothing but disabled options cannot spin.
  for (let attempt = 0; attempt < list.length; attempt += 1) {
    index = (index + step + list.length) % list.length
    if (!list[index].disabled) { activeIndex.value = index; return }
  }
}

function edge(first) {
  const list = visible.value
  const order = first ? list.map((_, i) => i) : list.map((_, i) => list.length - 1 - i)
  const found = order.find(index => !list[index].disabled)
  if (found !== undefined) activeIndex.value = found
}

function onTriggerKey(event) {
  if (['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Spacebar'].includes(event.key)) {
    if (!open.value) {
      event.preventDefault()
      show()
      return
    }
  }
  if (!open.value) return
  if (navigate(event)) return

  // Type-ahead, the one habit the native control taught everybody: press "m"
  // and land on Madrid. Only when there is no filter box to type into.
  if (!useSearch.value && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
    event.preventDefault()
    typeAhead(event.key)
  }
}

function onSearchKey(event) {
  navigate(event)
}

/** Shared arrow/Enter/Escape handling. Returns true when the key was ours. */
function navigate(event) {
  switch (event.key) {
    case 'ArrowDown': event.preventDefault(); move(1); return true
    case 'ArrowUp': event.preventDefault(); move(-1); return true
    case 'Home': event.preventDefault(); edge(true); return true
    case 'End': event.preventDefault(); edge(false); return true
    case 'PageDown': event.preventDefault(); for (let i = 0; i < 5; i += 1) move(1); return true
    case 'PageUp': event.preventDefault(); for (let i = 0; i < 5; i += 1) move(-1); return true
    case 'Enter':
    case ' ':
    case 'Spacebar':
      // A space inside the filter box is a space, not a choice.
      if (event.key !== 'Enter' && useSearch.value && event.target === search.value) return false
      event.preventDefault()
      if (active.value) choose(active.value)
      return true
    case 'Escape':
      event.preventDefault()
      close({ focusTrigger: true })
      return true
    case 'Tab':
      // No preventDefault: put focus back on the trigger and let the browser
      // carry on from there, so Tab out of an open list goes where it would
      // have gone if the list had never been open.
      close({ focusTrigger: true })
      return true
    default:
      return false
  }
}

let typed = ''
let typedAt = 0

function typeAhead(character) {
  const now = Date.now()
  typed = now - typedAt > 700 ? character : typed + character
  typedAt = now

  const term = typed.toLowerCase()
  const list = visible.value
  const from = activeIndex.value + (typed.length > 1 ? 0 : 1)

  for (let step = 0; step < list.length; step += 1) {
    const index = (from + step + list.length) % list.length
    const option = list[index]
    if (!option.disabled && String(option.label).toLowerCase().startsWith(term)) {
      activeIndex.value = index
      return
    }
  }
}

// A list whose options were replaced under it (a dependent dropdown reloading)
// must not keep pointing at a row that is no longer there.
watch(visible, (list) => {
  if (activeIndex.value >= list.length) activeIndex.value = list.length - 1
})
</script>

<style scoped>
/* ── The closed control ──────────────────────────────────────────────────── */
/* Same height, border and focus ring as UiInput: side by side in a form they
   have to read as the same family of control. */
.select { position: relative; }

.select__trigger {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  height: 38px;
  padding: 0 var(--space-3);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  font-size: var(--text-field);
  color: inherit;
  text-align: left;
  transition: border-color var(--fast), box-shadow var(--fast), background var(--fast);
}
.select__trigger:hover:not(:disabled) { border-color: var(--color-gray-300); }
.select__trigger:focus-visible {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-ring);
}
.select--open .select__trigger {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-ring);
}
.select--invalid .select__trigger { border-color: var(--color-danger); }
.select--invalid .select__trigger:focus-visible,
.select--invalid.select--open .select__trigger { box-shadow: 0 0 0 3px var(--color-danger-soft); }

.select__trigger:disabled {
  background: var(--color-surface-2);
  color: var(--color-text-mute);
  cursor: not-allowed;
}

.select__value {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.select__value--empty { color: var(--color-text-mute); }

.select__arrow {
  flex-shrink: 0;
  color: var(--color-text-mute);
  transition: color var(--fast), transform var(--base);
}
.select--open .select__arrow { color: var(--color-primary); transform: rotate(180deg); }

/* Present for the form, absent for the eye and for a screen reader. */
.select__mirror {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  pointer-events: none;
}

/* ── The open list ───────────────────────────────────────────────────────── */
/* Teleported to <body>, so it is positioned against the window and nothing it
   sits inside can clip it. Above the modal (100) and the assistant (140), below
   the toasts (200), which stay the top layer of the application. */
.select__pop {
  position: fixed;
  z-index: 190;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
}

.select__search {
  padding: var(--space-2);
  border-bottom: 1px solid var(--color-border);
}
.select__search-input {
  width: 100%;
  height: 32px;
  padding: 0 var(--space-2);
  background: var(--color-surface-2);
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  font-size: var(--text-field);
  outline: none;
}
.select__search-input:focus { border-color: var(--color-primary); background: var(--color-surface); }
.select__search-input::placeholder { color: var(--color-text-mute); }

.select__list {
  list-style: none;
  /* min-height:0 or the flex item refuses to shrink and scroll inside the cap. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-1);
  overscroll-behavior: contain;
}

.select__option {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-2);
  border-radius: var(--radius-sm);
  font-size: var(--text-md);
  cursor: pointer;
}
/* One highlight, driven by the keyboard and by the mouse alike: hovering moves
   the active row, so there are never two rows claiming to be "the one". */
.select__option--active { background: var(--color-gray-100); }
.select__option--selected { color: var(--color-primary); font-weight: 550; }
.select__option--disabled { color: var(--color-text-mute); cursor: not-allowed; }

.select__tick { flex-shrink: 0; margin-left: auto; }

.select__none {
  padding: var(--space-3);
  font-size: var(--text-sm);
  color: var(--color-text-mute);
  text-align: center;
}

/* Touch: fingers are wider than a mouse pointer. */
@media (pointer: coarse) {
  .select__trigger { height: 44px; }
  .select__option { padding: var(--space-3) var(--space-2); }
  .select__search-input { height: 40px; }
}

/* La hoja del móvil: ancho de pantalla, desde abajo, con fondo detrás. */
.select__backdrop {
  position: fixed;
  inset: 0;
  z-index: 189;
  background: rgb(0 0 0 / 0.35);
  touch-action: none;
}
/* Pegada al borde de abajo de lo que se ve: por encima del teclado si está
   (ui-kit utils/viewport.js), y nunca más alta que el 70 % de lo visible. */
.select__pop--sheet {
  left: 8px;
  right: 8px;
  bottom: calc(var(--keyboard-inset, 0px) + 8px);
  max-height: max(160px, calc(var(--app-height, 100dvh) * 0.7));
  border-radius: var(--radius-lg, 12px);
}
.select__pop--sheet .select__option {
  padding: var(--space-3);
  font-size: var(--text-field);
}
.select-pop-sheet-enter-active { transition: opacity var(--fast), transform var(--base); }
.select-pop-sheet-leave-active { transition: opacity var(--fast), transform var(--fast); }
.select-pop-sheet-enter-from,
.select-pop-sheet-leave-to { opacity: 0; transform: translateY(24px); }

/* It grows out of the control, so it starts slightly collapsed towards it. */
.select-pop-down-enter-active,
.select-pop-up-enter-active { transition: opacity var(--fast), transform var(--base); }
.select-pop-down-leave-active,
.select-pop-up-leave-active { transition: opacity var(--fast), transform var(--fast); }

.select-pop-down-enter-from,
.select-pop-down-leave-to { opacity: 0; transform: translateY(-6px) scale(0.98); }
.select-pop-up-enter-from,
.select-pop-up-leave-to { opacity: 0; transform: translateY(6px) scale(0.98); }
</style>
